import { RootActionTypes } from '@redux/modules/actionTypes';
import { RootActions } from 'types/state';
import { STATUS_SUBMITTED } from 'constants/enums/applicationStatuses';
import * as ActionTypes from './actionTypes';
import {
  FETCH_CHECK_STATUS_REQUEST,
  FETCH_CHECK_STATUS_SUCCESS,
  FETCH_CHECK_STATUS_FAILURE,
  SUBMIT_IDENTITY_VERIFICATION_REQUEST,
  SUBMIT_IDENTITY_VERIFICATION_SUCCESS,
  SUBMIT_IDENTITY_VERIFICATION_FAILURE,
  FETCH_IDENTITY_STATUS_REQUEST,
  FETCH_IDENTITY_STATUS_SUCCESS,
  FETCH_IDENTITY_STATUS_FAILURE,
  FETCH_SET_ACCOUNT_REQUEST,
  FETCH_SET_ACCOUNT_SUCCESS,
  FETCH_SET_ACCOUNT_FAILURE,
  FETCH_STATUS_REQUEST,
  FETCH_STATUS_FAILURE,
  FETCH_STATUS_SUCCESS,
  FETCH_FORM_REQUEST,
  FETCH_FORM_FAILURE,
  FETCH_FORM_SUCCESS,
  FETCH_ENVIRONMENT_REQUEST,
  FETCH_ENVIRONMENT_SUCCESS,
  FETCH_ENVIRONMENT_FAILURE,
  FETCH_API_VERSION_REQUEST,
  FETCH_API_VERSION_SUCCESS,
  FETCH_API_VERSION_FAILURE,
  FETCH_SERVER_TIME_REQUEST,
  FETCH_SERVER_TIME_SUCCESS,
  FETCH_SERVER_TIME_FAILURE,
  FETCH_INSTITUTION_SEARCH_REQUEST,
  FETCH_INSTITUTION_SEARCH_SUCCESS,
  FETCH_INSTITUTION_SEARCH_FAILURE,
  FETCH_ANNOUNCEMENTS_REQUEST,
  FETCH_ANNOUNCEMENTS_SUCCESS,
  FETCH_ANNOUNCEMENTS_FAILURE
} from './actionTypes';
import {
  getApiVersionDefault, getClientVersion, ClientVersion, ApiVersion
} from '../../../util/version';

export interface APIMethodState {
  isLoading: boolean
  response?: object
  hasError: boolean
  formLoaded?: boolean
}

export interface APIState {
  fetchCheckStatus: APIMethodState,
  fetchForm: APIMethodState
  fetchIdentityStatus: APIMethodState
  fetchSetAccount: APIMethodState
  fetchStatus: APIMethodState
  fetchEnvironment: APIMethodState
  fetchApiVersion: APIMethodState
  fetchServerTime: APIMethodState
  fetchInstitutionSearch: APIMethodState
  fetchAnnouncements: APIMethodState
  apiVersion: ApiVersion,
  clientVersion: ClientVersion,
  submitIdentityVerification: APIMethodState,
  setLockedForm?: boolean,
}

export const initialState: APIState = {
  fetchCheckStatus: {
    isLoading: false,
    hasError: false,
  },
  fetchForm: {
    isLoading: false,
    hasError: false,
    formLoaded: false,
  },
  fetchIdentityStatus: {
    isLoading: false,
    hasError: false,
  },
  fetchSetAccount: {
    isLoading: false,
    hasError: false,
  },
  fetchStatus: {
    isLoading: false,
    hasError: false,
  },
  fetchEnvironment: {
    isLoading: false,
    hasError: false,
  },
  fetchApiVersion: {
    isLoading: false,
    hasError: false,
  },
  fetchServerTime: {
    isLoading: false,
    hasError: false,
  },
  fetchInstitutionSearch: {
    isLoading: false,
    hasError: false,
  },
  fetchAnnouncements: {
    isLoading: false,
    hasError: false,
  },
  submitIdentityVerification: {
    isLoading: false,
    hasError: false,
  },
  apiVersion: getApiVersionDefault(),
  clientVersion: getClientVersion(),
  setLockedForm: false,
};

const methodKeys = [
  {
    key: 'fetchCheckStatus',
    data: [FETCH_CHECK_STATUS_REQUEST, FETCH_CHECK_STATUS_SUCCESS, FETCH_CHECK_STATUS_FAILURE],
  },
  {
    key: 'fetchIdentityStatus',
    data: [FETCH_IDENTITY_STATUS_REQUEST, FETCH_IDENTITY_STATUS_SUCCESS, FETCH_IDENTITY_STATUS_FAILURE],
  },
  {
    key: 'fetchSetAccount',
    data: [FETCH_SET_ACCOUNT_REQUEST, FETCH_SET_ACCOUNT_SUCCESS, FETCH_SET_ACCOUNT_FAILURE],
  },
  {
    key: 'fetchStatus',
    data: [FETCH_STATUS_REQUEST, FETCH_STATUS_FAILURE, FETCH_STATUS_SUCCESS],
  },
  {
    key: 'fetchForm',
    data: [FETCH_FORM_REQUEST, FETCH_FORM_FAILURE, FETCH_FORM_SUCCESS],
  },
  {
    key: 'fetchEnvironment',
    data: [FETCH_ENVIRONMENT_REQUEST, FETCH_ENVIRONMENT_FAILURE, FETCH_ENVIRONMENT_SUCCESS],
  },
  {
    key: 'fetchApiVersion',
    data: [FETCH_API_VERSION_REQUEST, FETCH_API_VERSION_FAILURE, FETCH_API_VERSION_SUCCESS],
  },
  {
    key: 'fetchServerTime',
    data: [FETCH_SERVER_TIME_REQUEST, FETCH_SERVER_TIME_SUCCESS, FETCH_SERVER_TIME_FAILURE],
  },
  {
    key: 'fetchInstitutionSearch',
    data: [FETCH_INSTITUTION_SEARCH_REQUEST, FETCH_INSTITUTION_SEARCH_SUCCESS, FETCH_INSTITUTION_SEARCH_FAILURE],
  },
  {
    key: 'fetchAnnouncements',
    data: [FETCH_ANNOUNCEMENTS_REQUEST, FETCH_ANNOUNCEMENTS_SUCCESS, FETCH_ANNOUNCEMENTS_FAILURE],
  },
  {
    key: 'submitIdentityVerification',
    data: [SUBMIT_IDENTITY_VERIFICATION_REQUEST, SUBMIT_IDENTITY_VERIFICATION_SUCCESS, SUBMIT_IDENTITY_VERIFICATION_FAILURE],
  },
];

const getKey = (type: string) => {
  let key = '';

  methodKeys.forEach((item) => {
    if (item.data.includes(type)) { key = item.key; }
  });

  return key;
};

// eslint-disable-next-line default-param-last
const reducer = (state: APIState = initialState, action: RootActions): APIState => {
  const key = getKey(action.type);
  switch (action.type) {
    case ActionTypes.FETCH_CHECK_STATUS_REQUEST:
    case ActionTypes.FETCH_ENVIRONMENT_REQUEST:
    case ActionTypes.FETCH_IDENTITY_STATUS_REQUEST:
    case ActionTypes.FETCH_SET_ACCOUNT_REQUEST:
    case ActionTypes.FETCH_STATUS_REQUEST:
    case ActionTypes.FETCH_FORM_REQUEST:
    case ActionTypes.FETCH_INSTITUTION_SEARCH_REQUEST:
    case ActionTypes.FETCH_ANNOUNCEMENTS_REQUEST:
    case ActionTypes.SUBMIT_IDENTITY_VERIFICATION_REQUEST:
      return {
        ...state,
        [key]: {
          isLoading: true,
          response: null,
          hasError: false,
        },
      };
    case ActionTypes.FETCH_CHECK_STATUS_SUCCESS:
      return {
        ...state,
        fetchCheckStatus: {
          isLoading: false,
          hasError: false,
          response: action.payload,
        },
      };
    case ActionTypes.FETCH_IDENTITY_STATUS_SUCCESS:
      return {
        ...state,
        fetchIdentityStatus: {
          isLoading: false,
          hasError: false,
          response: action.payload as object,
        },
      };
    case ActionTypes.FETCH_SET_ACCOUNT_SUCCESS:
      return {
        ...state,
        fetchSetAccount: {
          isLoading: false,
          hasError: false,
          response: undefined,
        },
        setLockedForm: (action.status === STATUS_SUBMITTED),
      };
    case ActionTypes.FETCH_STATUS_SUCCESS:
      return {
        ...state,
        fetchStatus: {
          isLoading: false,
          hasError: false,
          response: action.payload,
        },
      };
    case ActionTypes.FETCH_ENVIRONMENT_SUCCESS:
      return {
        ...state,
        fetchEnvironment: {
          isLoading: false,
          hasError: false,
          response: action.payload,
        },
      };
    case ActionTypes.FETCH_INSTITUTION_SEARCH_SUCCESS:
      return {
        ...state,
        fetchInstitutionSearch: {
          isLoading: false,
          hasError: false,
          response: action.payload,
        },
      };
    case ActionTypes.FETCH_API_VERSION_SUCCESS:
      return {
        ...state,
        fetchApiVersion: {
          isLoading: false,
          hasError: false,
          response: action.payload,
        },
        apiVersion: (action.payload || getApiVersionDefault()),
      };
    case ActionTypes.FETCH_FORM_SUCCESS:
      return {
        ...state,
        [key]: {
          isLoading: false,
          response: action.payload.data,
          hasError: false,
          formLoaded: true,
        },
      };
    case ActionTypes.FETCH_SERVER_TIME_SUCCESS:
      return {
        ...state,
        fetchServerTime: {
          isLoading: false,
          hasError: false,
          response: action.payload,
        },
      };
    case ActionTypes.FETCH_ANNOUNCEMENTS_SUCCESS:
      return {
        ...state,
        fetchAnnouncements: {
          isLoading: false,
          hasError: false,
          response: action.payload,
        },
      };
    case ActionTypes.SUBMIT_IDENTITY_VERIFICATION_SUCCESS:
      return {
        ...state,
        submitIdentityVerification: {
          isLoading: false,
          hasError: false,
          response: action.payload,
        },
      };
    case ActionTypes.FETCH_CHECK_STATUS_FAILURE:
    case ActionTypes.FETCH_IDENTITY_STATUS_FAILURE:
    case ActionTypes.FETCH_SERVER_TIME_FAILURE:
    case ActionTypes.FETCH_SET_ACCOUNT_FAILURE:
    case ActionTypes.FETCH_ENVIRONMENT_FAILURE:
    case ActionTypes.FETCH_STATUS_FAILURE:
    case ActionTypes.FETCH_FORM_FAILURE:
    case ActionTypes.FETCH_INSTITUTION_SEARCH_FAILURE:
    case ActionTypes.FETCH_ANNOUNCEMENTS_FAILURE:
    case ActionTypes.SUBMIT_IDENTITY_VERIFICATION_FAILURE:
      return {
        ...state,
        [key]: {
          isLoading: false,
          response: action.payload.error,
          hasError: true,
        },
      };
    case RootActionTypes.authentication.FETCH_LOGIN_SUCCESS: {
      return {
        ...initialState,
      };
    }
    case RootActionTypes.authentication.FETCH_LOGOUT_SUCCESS: {
      return {
        ...state,
        fetchForm: initialState.fetchForm,
        fetchStatus: initialState.fetchStatus,
      };
    }
    default:
      return state;
  }
};

export default reducer;
